<script>
  import { enhance, applyAction } from '$app/forms'
  import { asText } from '@prismicio/client'
  import { goto } from '$app/navigation'
  import { page } from '$app/stores'

  import TripSearchForm from '$lib/TripSearchForm.svelte'
  import PassBundleShop from '$lib/PassBundleShop.svelte'
  import BlogPostMeta from '$lib/BlogPostMeta.svelte'
  import Illustrated from '$lib/Illustrated.svelte'
  import PageHeading from '$lib/PageHeading.svelte'
  import ArticleList from '$lib/ArticleList.svelte'
  import ListColumns from '$lib/ListColumns.svelte'
  import Blockquote from '$lib/Blockquote.svelte'
  import ImageBoxes from '$lib/ImageBoxes.svelte'
  import ContactForm from './ContactForm.svelte'
  import Promotion from '$lib/Promotion.svelte'
  import Inventory from '$lib/Inventory.svelte'
  import IconBoxes from '$lib/IconBoxes.svelte'
  import { prefix } from '$lib/utils/prefix.js'
  import RichText from '$lib/RichText.svelte'
  import { resolve } from '$lib/prismic.js'
  import Figure from '$lib/Figure.svelte'
  import Button from '$lib/Button.svelte'
  import Media from '$lib/Media.svelte'
  import Blurb from '$lib/Blurb.svelte'
  import Image from '$lib/Image.svelte'
  import Html from '$lib/Html.svelte'
  import { text } from '$lib/text.js'
  import {
    passengersFromParams,
    passengersToParams
  } from '$lib/utils/passengers.js'

  export let slices = []

  const scrollIntoView = (event) => {
    const { hash } = event.currentTarget.hash
    const el = hash && document.querySelector(hash)
    if (!el) return
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
    event.preventDefault()
  }

  let submitting = false
  const onsubmit = () => {
    submitting = true
    return async ({ result }) => {
      try {
        await applyAction(result)
      } finally {
        submitting = false
      }
    }
  }
</script>

<div class="slices">
  {#each slices as slice}
    {@const { slice_type, primary, items } = slice}
    <div
      class="slice slice-{slice_type}"
      class:gray={slice.primary.gray}
      class:stacked={slice.primary.stacked}
      class:stackdown={slice.primary.stackdown}>
      {#if slice_type === 'body'}
        <Html center={primary.middle} size={primary.larger ? 'large' : null}>
          <RichText content={primary.content} />
        </Html>
        {#if items}
          {#each items as item}
            <Html center={item.middle} size={item.larger ? 'large' : null}>
              <RichText content={item.content} />
            </Html>
          {/each}
        {/if}
      {/if}

      {#if slice_type === 'section_title'}
        <Html size="large">
          <RichText content={primary.content} />
        </Html>
      {/if}

      {#if slice_type === 'icon_grid' && items.length}
        <IconBoxes
          items={items.map((item) => {
            const title = asText(item.icon_title)
            return {
              title,
              content: item.icon_body,
              link: resolve(item.icon_link),
              overflow: item.overflow,
              icon: item.icon.url
                ? {
                    src: item.icon.url,
                    alt: item.icon.alt || title,
                    width: item.icon.dimensions.width,
                    height: item.icon.dimensions.height
                  }
                : null
            }
          })}
          let:item>
          <RichText content={item.content} />
        </IconBoxes>
      {/if}

      {#if slice_type === 'scroll_hook'}
        <div style="margin-top: -3rem; height: 3rem;" id={primary.id}></div>
      {/if}

      {#if slice_type === 'callout'}
        <PageHeading>
          <RichText slot="promotion" content={primary.text} />
          <svelte:fragment slot="content">
            {#if primary.scroll_hook && primary.scroll_text}
              <Button
                size="lg"
                href="#{primary.scroll_hook}"
                onclick{scrollIntoView}>
                {primary.scroll_text}
              </Button>
            {/if}
          </svelte:fragment>
        </PageHeading>
      {/if}

      {#if slice_type === 'quote'}
        <Blockquote middle>
          <RichText content={primary.text} />
          <RichText slot="cite" content={primary.cite} />
        </Blockquote>
      {/if}

      {#if slice_type === 'illustrated_text'}
        {@const image = primary.image?.url ? primary.image : null}
        <Illustrated
          flip={primary.flip}
          small={primary.small}
          video={primary.video}
          {image}>
          <Html slot="body">
            <RichText content={primary.content} />
          </Html>
        </Illustrated>
      {/if}

      {#if slice_type === 'page_heading'}
        {@const { heading, preamble, tagline, promotion } = primary}
        <PageHeading
          title={asText(heading)}
          hidden={promotion.length}
          large={primary.large}>
          <RichText slot="tagline" content={tagline} />
          <RichText slot="body" content={preamble} />
          <RichText slot="promotion" content={promotion} />
          <svelte:fragment slot="content">
            {#if items.length === 1}
              {#each items.filter((item) => item.button_text) as item}
                {@const { button_hook: hook, button_text: children } = item}
                {@const href = resolve(item.button_link)}
                {#if hook || href}
                  <Button
                    size="lg"
                    href={href || `#${hook}`}
                    onclick={(event) =>
                      hook ? scrollIntoView(event) : undefined}>
                    {children}
                  </Button>
                {/if}
              {/each}
            {:else}
              <Html size="large">
                <p>
                  {#each items.filter((item) => item.button_text) as item, index}
                    {@const { button_hook: hook, button_text: children } = item}
                    {@const href = resolve(item.button_link)}
                    {#if hook || href}
                      {#if index >= 1}<br />
                      {/if}
                      <a
                        href={href || `#${hook}`}
                        on:click={hook ? scrollIntoView : undefined}>
                        {children}
                      </a>
                    {/if}
                  {/each}
                </p>
              </Html>
            {/if}
          </svelte:fragment>
        </PageHeading>
      {/if}

      {#if slice_type === 'banner'}
        {@const href = resolve(primary.cta_link)}
        {@const supportingHref = resolve(primary.cta_supporting_link)}
        <Media
          backdrop={primary.backdrop}
          white={primary.white}
          image={primary.image}
          video={primary.video}>
          <div class:trip-search-form={primary.include_trip_search_form}>
            <div class="body">
              <Html>
                <RichText content={primary.content} />
              </Html>
              <div class="actions">
                {#if slice.primary.cta_text}
                  <Button {href} onclick={scrollIntoView}>
                    {primary.cta_text}
                  </Button>
                {/if}
                {#if slice.primary.cta_supporting_text}
                  <Html>
                    <a href={supportingHref}>
                      {primary.cta_supporting_text}
                    </a>
                  </Html>
                {/if}
              </div>
            </div>
            {#if primary.include_trip_search_form}
              <TripSearchForm
                geolocate
                onsearch={({
                  origin,
                  destination,
                  departureDate,
                  passengers
                }) => {
                  const params = new URLSearchParams({
                    ddate: departureDate,
                    origin: origin.uid,
                    destination: destination.uid
                  })

                  for (const value of passengersToParams(passengers)) {
                    params.append('passenger', value)
                  }

                  goto(`${$prefix}/search?${params}`)
                }} />
            {/if}
          </div>
        </Media>
      {/if}

      {#if slice_type === 'pages_link_grid'}
        {@const heading = asText(primary.heading)}
        {@const links = items
          .map((item) => item.link)
          .filter((link) => link.data)}
        {#if heading}
          <Html>
            <h2 class="h3">{heading}</h2>
            <br />
          </Html>
        {/if}
        <div class="blurbs">
          {#each links as doc}
            {@const hero = doc.data.body?.find((slice) =>
              slice.slice_type.includes('hero')
            )}
            {@const image = doc.data.featured_image?.url
              ? doc.data.featured_image
              : hero?.primary.image}
            {@const title = asText(doc.data.title)}
            <Blurb
              link={{
                href: resolve(doc),
                label: $text`Read post`
              }}>
              <Html slot="content" size="small">
                <h3>{title}</h3>
                <p class="link">Read post</p>
              </Html>
              {#if image?.url}
                <Image
                  sizes="(min-width: 1200px) 8.5rem, 6rem"
                  src={image.url}
                  variants={[100, 150, 200, 300]}
                  aspect={1}
                  transforms="q_50"
                  alt={image.alt || title}
                  {...image.dimensions} />
              {/if}
            </Blurb>
          {/each}
        </div>
      {/if}

      {#if slice_type === 'image' || slice_type === 'photo'}
        <Figure image={primary.image} />
        <div style="margin-top: 0.5rem;">
          <Html size="small" muted>
            <RichText content={primary.caption} />
          </Html>
        </div>
      {/if}

      {#if slice_type === 'image_grid'}
        {#if items && items.length}
          <ImageBoxes
            items={items.map((item) => ({
              link: resolve(item.link),
              title: asText(item.image_title),
              image: item.image,
              linkText: item.link_text ? item.link_text : $text`Read more`,
              content: item.text
            }))}
            let:content>
            <RichText {content} />
          </ImageBoxes>
        {/if}
      {/if}

      {#if slice_type === 'article_list'}
        {#if items && items.length}
          <ArticleList
            expanded={primary.expanded}
            items={items.map((item) => {
              const data = item.page.data
              const slices = data.body
              const preamble = slices.find(
                (slice) => slice.slice_type === 'page_heading'
              )?.primary.preamble
              const body = slices.find((slice) => slice.slice_type === 'body')
                ?.primary.content
              const meta = slices.find(
                (slice) => slice.slice_type === 'blog_post_preamble'
              )?.primary

              return {
                link: resolve(item.page),
                title: asText(data.title),
                description: preamble ? asText(preamble) : data.description,
                body: primary.expanded ? body : null,
                image: data.featured_image,
                date: meta?.publish_date,
                author: {
                  name: meta?.author_name,
                  image: meta?.author_image
                }
              }
            })} />
        {/if}
      {/if}

      {#if slice_type === 'blog_post_preamble'}
        <BlogPostMeta
          date={primary.publish_date}
          author={primary.author_name}
          portrait={primary.author_image} />
      {/if}

      {#if slice_type === 'divider'}
        <hr />
      {/if}

      {#if slice_type === 'operators'}
        <Illustrated flip={primary.flip} video={primary.video}>
          <Inventory
            slot="illustration"
            items={items
              .filter(
                (item) =>
                  item.operator.id &&
                  item.operator.data?.logo?.url &&
                  !item.operator.isBroken
              )
              .map((item) => {
                const { width, height } = item.operator.data.logo.dimensions
                return {
                  width,
                  height,
                  src: item.operator.data.logo.url,
                  alt: asText(item.operator.data.name)
                }
              })} />
          <Html slot="body">
            <RichText content={primary.content} />
          </Html>
        </Illustrated>
      {/if}

      {#if slice_type === 'trip_search_form'}
        <div class="trip-search-form">
          <Html>
            <RichText content={primary.content} />
          </Html>
          <TripSearchForm
            onsearch={({ origin, destination, departureDate, passengers }) => {
              const params = new URLSearchParams({
                ddate: departureDate,
                origin: origin.uid,
                destination: destination.uid
              })

              for (const value of passengersToParams(passengers)) {
                params.append('passenger', value)
              }

              goto(`${$prefix}/search?${params}`)
            }} />
        </div>
      {/if}

      {#if slice_type === 'list_columns' && items && items.length}
        <ListColumns icon={primary.icon} color={primary.color} {items} let:item>
          <RichText content={item.item} />
        </ListColumns>
      {/if}

      {#if slice_type === 'button'}
        <Button
          size="xl"
          href={resolve(primary.link)}
          target={primary.link.target === '_blank' ? '_blank' : null}
          onclick={scrollIntoView}>
          {primary.text}
        </Button>
      {/if}

      {#if slice_type === 'interrail_shop'}
        <PassBundleShop
          id={slice.id}
          product={primary.product.toLowerCase().replace(/\s+/g, '-')}
          passengers={passengersFromParams($page.url.searchParams)}
          badge={primary.badge_text}
          campaignCode={primary.campaign_id}
          expanded={primary.show_all_passes || undefined}>
          {#if primary.current_deal_heading || primary.current_deal_image?.url || primary.current_deal_text}
            <div>
              <span class="deal-label">{$text`Current Deals`}</span>
              <div class="deal">
                {#if primary.current_deal_image?.url}
                  <Image
                    {...primary.current_deal_image.dimensions}
                    src={primary.current_deal_image.url}
                    alt={primary.current_deal_image.alt}
                    variants={[80, 160]}
                    class="deal-image"
                    sizes="5rem" />
                {/if}
                <div class="text">
                  {#if primary.current_deal_heading}
                    <h3 class="deal-heading">
                      {primary.current_deal_heading}
                    </h3>
                  {/if}
                  {#if primary.current_deal_text}
                    <p class="deal-text">{primary.current_deal_text}</p>
                  {/if}
                </div>
              </div>
            </div>
          {/if}
        </PassBundleShop>
      {/if}

      {#if slice_type === 'contact_form'}
        {@const form = $page.form?.[slice.id]}
        {@const thread = primary.twist_channel_link.match(
          /twist.com\/a\/(\w+)\/ch\/(\w+)\/?$/
        )}
        {@const primaryKey = items.find((item) => item.is_primary_key)}
        {#if thread}
          <form method="POST" action="?/contact" use:enhance={onsubmit}>
            <input type="hidden" name="form" value={slice.id} />
            <input type="hidden" name="team" value={thread[1]} />
            <input type="hidden" name="channel" value={thread[2]} />
            {#if primaryKey}
              <input type="hidden" name="title" value={primaryKey.label} />
            {/if}
            <ContactForm
              state={submitting
                ? 'SUBMITTING'
                : form?.success
                  ? 'SUCCESS'
                  : $page.status === 400
                    ? 'ERROR'
                    : 'IDLE'}
              buttonText={primary.button_text}
              fields={items.map((item) => ({
                value: form?.fields?.[item.name || item.label],
                name: item.label,
                ...item
              }))}>
              <Html>
                <RichText content={primary.description} />
              </Html>
              <Html size="small" slot="success">
                <RichText content={primary.success_message} />
              </Html>
            </ContactForm>
          </form>
        {/if}
      {/if}

      {#if slice_type === 'signup_form'}
        {@const form = $page.form?.[slice.id]}
        <form method="POST" action="?/signup" use:enhance={onsubmit}>
          <input type="hidden" name="form" value={slice.id} />
          <div class="signup">
            <ContactForm
              state={submitting
                ? 'SUBMITTING'
                : form?.success
                  ? 'SUCCESS'
                  : $page.status === 400
                    ? 'ERROR'
                    : 'IDLE'}
              buttonText="Join now"
              fields={[
                {
                  name: 'contact_name',
                  label: 'Your name',
                  type: 'text',
                  required: true
                },
                {
                  name: 'gotcha',
                  honeypot: true,
                  label: 'Your last name',
                  type: 'text'
                },
                {
                  name: 'company_name',
                  label: 'Company name',
                  type: 'text',
                  required: true
                },
                {
                  name: 'email',
                  label: 'Email address',
                  type: 'email',
                  required: true
                },
                {
                  name: 'How did you hear about us?',
                  label: 'How did you hear about us?',
                  type: 'text',
                  placeholder: 'Online search, social media, press, …',
                  required: false
                },
                {
                  name: 'If you wish, please tell us a bit about your company',
                  label: 'If you wish, please tell us a bit about your company',
                  type: 'textarea',
                  required: false
                }
              ]}>
              <Html>
                <RichText content={primary.content} />
              </Html>
              <Html size="small" slot="success">
                <RichText content={primary.success_message} />
              </Html>

              {#if items}
                <ListColumns
                  icon="checkmark"
                  color="green"
                  row
                  small
                  {items}
                  let:item>
                  <RichText content={item.list_item} />
                </ListColumns>
              {/if}

              {#if primary.extra_content}
                <div class="box">
                  <Html size="small">
                    <RichText content={primary.extra_content} />
                  </Html>
                </div>
              {/if}
            </ContactForm>
          </div>
        </form>
      {/if}

      {#if slice_type === 'advertisement'}
        {@const href = resolve(primary.link)}
        <Promotion
          label={primary.label}
          background={primary.background_color}
          overflow={primary.image_overflow}
          image={primary.image}
          link={href
            ? {
                href,
                text: primary.link_text || $text`Read more`
              }
            : undefined}>
          <RichText content={primary.heading} />
        </Promotion>
      {/if}
    </div>
  {/each}
</div>

<style>
  .slices {
    --item-padding: 5rem;
    --item-padding-sm: 2rem;
    --current-background: var(--background);

    position: relative;
  }

  @media (min-width: 600px) {
    .slices {
      --item-padding: 6rem;
    }
  }

  @media (min-width: 1000px) {
    .slices {
      --item-padding-sm: 3.5rem;
      --item-padding: 8rem;
    }
  }

  .slice {
    --gutter: max(
      calc((100vw - var(--container-large-width)) / 2),
      var(--document-margin)
    );

    width: 100%;
    padding: var(--item-padding) var(--gutter);
  }

  @supports (padding-left: env(safe-area-inset-left)) {
    .slice {
      padding-left: calc(var(--gutter) + env(safe-area-inset-left));
      padding-right: calc(var(--gutter) + env(safe-area-inset-right));
    }
  }

  .slice:empty {
    display: none;
    padding: 0;
  }

  .gray {
    --current-background: rgb(var(--rgb-bright-gray));

    background: var(--current-background);
  }

  .overlap {
    --rgb-text: var(--rgb-white);
    --current-background: transparent;

    padding-top: 2.5rem;
  }

  .slice + :global(.slice) {
    /* Not sure if this one is in use */
    padding-top: 0 !important;
  }

  /* Make sure we get a double margin between items with different backgrounds */

  .gray + :global(.slice:not(.gray)),
  .slice:not(.gray) + :global(.gray) {
    padding-top: var(--item-padding) !important;
  }

  /* Handle dividers with gray above and below */

  .gray + .slice-divider {
    padding-top: 0 !important;
  }

  .gray + .slice-divider:has(:global(+ .gray)) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .slice-divider:last-child {
    padding-bottom: 0 !important;
  }

  /* Specific slice margins */

  .slice-page_heading:first-child {
    padding-top: 15svh;
  }

  .slice-page_heading:has(:global(+ .slice-body)) {
    padding-bottom: 4rem !important;
  }

  .slice-scroll_hook {
    padding: 0 !important;
  }

  .slice-section_title {
    padding-bottom: 2rem !important;
  }

  .slice-body:has(:global(+ .slice-quote)),
  .slice-quote:has(:global(+ .slice-body)) {
    padding-bottom: 2rem !important;
  }

  .slice-body:has(:global(+ .slice-button)),
  .slice-button:has(:global(+ .slice-body)) {
    padding-bottom: 2rem !important;
  }

  .slice-page_heading:has(:global(+ .slice-article_list)) {
    padding-bottom: var(--item-padding-sm) !important;
  }

  /* Handle post meta */

  .slice-blog_post_preamble {
    padding-bottom: calc(var(--item-padding-sm) + 1rem) !important;
  }

  .slice-blog_post_preamble:first-child {
    padding-top: 0 !important;
  }

  .slice-page_heading:has(:global(+ .slice-blog_post_preamble)) {
    padding-bottom: min(var(--item-padding-sm), 1.75rem) !important;
  }

  /* Pull up banner when background turns gray */

  .slice:not(.gray):has(:global(+ .slice-banner.gray)) {
    padding-bottom: calc(var(--item-padding) * 2) !important;
  }

  .slice:not(.gray) + .slice-banner.gray > :global(*) {
    margin-top: calc(var(--item-padding) * 2 * -1) !important;
  }

  /* Pull up banner when background turns white */

  .gray:has(:global(+ .slice-banner:not(.gray))) {
    padding-bottom: calc(var(--item-padding) * 2) !important;
  }

  .gray + .slice-banner:not(.gray) {
    margin-top: calc(var(--item-padding) * 2 * -1);
  }

  .gray:last-child {
    /* Fix double margin with footer */
    padding-bottom: var(--item-padding-sm) !important;
  }

  /* Random layouts */

  .slice-banner .body {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .slice-banner .actions {
    --current-font-size: 0.875rem;

    display: flex;
    gap: 0.75rem 1rem;
    flex-wrap: wrap;
    align-items: center;
  }

  @media (width > 420px) {
    .slice-banner .actions {
      --current-font-size: 1.125rem;

      gap: 0.75rem 1.5rem;
    }
  }

  @media (width > 1000px) {
    .slice-banner .actions {
      --current-font-size: 1.25rem;
    }
  }

  .slice-banner :global(h2) {
    margin-bottom: 1.5rem !important;
  }

  /* Interrail bundle shop */

  .deal-label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1rem;
    color: rgb(var(--rgb-muted));
  }

  .deal {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
  }

  .deal :global(.deal-image) {
    width: 5rem;
  }

  .deal-heading {
    font-weight: bold;
    font-size: 1.25rem;
  }

  .deal-text {
    font-size: 1rem;
    color: rgb(var(--rgb-muted));
  }

  /* Misc helpers */

  .trip-search-form {
    display: grid;
    gap: clamp(1.5rem, 4vw, 2.25rem);
    align-items: start;
    width: 100%;
  }

  @media (min-width: 800px) {
    .trip-search-form {
      grid-template-columns: 1fr 1fr;
    }
  }

  .blurbs {
    display: grid;
    gap: 1.5rem;
  }

  @media (min-width: 700px) {
    .blurbs {
      gap: 2.5rem;
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (min-width: 1250px) {
    .blurbs {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  /* Signup form */

  .signup :global(.body) {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    text-wrap: balance;
  }

  .signup :global(.body .box) {
    padding: 1.5rem min(max(var(--document-margin), 1.5rem), 2rem);
    background: rgb(var(--rgb-bright-gray));
    border-radius: var(--border-radius-md);
  }

  @media (width < 1001px) {
    .signup :global(.body .box) {
      display: none;
    }
  }
</style>
